import React from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

// Component imports from external API
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import _ from "underscore";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

//Home brewed Components
import DeleteFromMyAssayButton from "./ButtonsAndModals/DeleteFromMyAssayButton";
import AddToCartAndQuoteButton from "./ButtonsAndModals/AddToCartAndQuoteButton";
// Utils
import {
  formatAssayApplicationType,
  formatStatus,
  formatDateTime,
  formatAssayFormat,
  formatUniversalAssayName,
  formatFluorophores,
  mapAppType,
} from "../../util/format.js";

/*
  Components: `AssayRow`.
  Main Component: `AssayRow`.
  Usage: Used for populating Row in My Assay Page.
  Sub-Compoenents: `AddToCartAndQuoteButton` & `DeleteFromMyAssayButton`;
  Usage: Used to Delete, Add to Cart and Quote Buttons;
*/

class AssayRow extends React.Component {
  render() {
    const { row, actions, classes, evenOddClass } = this.props;
    const { id } = row;
    const viewedClass =
      row.viewed === 1 || row.status.toLowerCase() !== "done" ? "" : "rowBold";

    const hasSupplement =
      row &&
      row.status &&
      row.status.toLowerCase() !== "undesignable" &&
      row.uniAssayId &&
      true;

    const supplementClass = hasSupplement ? "has-supplement" : "no-supplement";

    return (
      <React.Fragment>
        <TableRow
          className={classNames(
            classes.row,
            viewedClass,
            evenOddClass,
            supplementClass
          )}
        >
          <TableCell
            className={classNames(
              row && row.assayFormat && row.assayFormat.toLowerCase()
            )}
            padding="none"
            scope="row"
          >
            &nbsp;
          </TableCell>
          {/* Assay Name */}
          <TableCell scope="row" padding="none">
            {
              <div>
                <Link
                  to={`/digital-assays/assay-detail/${row.assayId}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    row.viewed === 0 && actions.assayViewed(row.id);
                  }}
                >
                  <strong>{row && (row.assayName || row.assayId)}</strong>
                </Link>
              </div>
            }

            {row.status.toLowerCase() !== "done" && (
              <strong>{row && (row.assayName || row.assayId)}</strong>
            )}
          </TableCell>

          {/* Target Name for the assay*/}
          <TableCell padding="none">
            <div style={{ maxWidth: 80 }}>{row && row.targetName}</div>
          </TableCell>

          {/* Created At time for the assay*/}
          <TableCell padding="none">
            {/* Data format needs to be changed to match the previous application*/}
            <div style={{ minWidth: 65 }}>
              {row && row.createdAt && formatDateTime(row.createdAt)}
            </div>
          </TableCell>

          {/* Application Type for the assay */}
          <TableCell
            padding="none"
            data-applicationtype={row.applicationType}
            data-assayformat={row.assayFormat}
          >
            {row && row.applicationType && mapAppType(row.applicationType)}
            {row &&
              row.assayFormat &&
              row.applicationType === "MutationDetection" &&
              row.assayFormat === "SingleTube" &&
              formatAssayFormat(row.assayFormat)}
          </TableCell>

          {/* Processing status for the assay */}
          <TableCell padding="none">
            <div className="text-capitalize">
              {row &&
                row.status &&
                row.status.toLowerCase() === "done" &&
                formatStatus(row.status.toLowerCase())}
              {row &&
                row.status &&
                row.status.toLowerCase() === "undesignable" && (
                  <OverlayTrigger
                    trigger="focus"
                    placement="right"
                    overlay={
                      <Popover
                        title="Undesignable Assay"
                        className="popover-right-borders"
                      >
                        Due to complexities in the target sequence we were
                        unable to design an assay for this target.
                      </Popover>
                    }
                  >
                    <Link to="#">{formatStatus(row.status)}</Link>
                  </OverlayTrigger>
                )}
              {row &&
                row.status.toLowerCase() !== "done" &&
                row.status.toLowerCase() !== "undesignable" && (
                  <OverlayTrigger
                    trigger="focus"
                    placement="right"
                    overlay={
                      <Popover
                        title="Undesignable Assay"
                        className="popover-right-borders"
                      >
                        We do not have pre-designed assays for this gene. We
                        currently do not offer automated designs for new gene
                        expression assays. Please contact our representatives
                        for further assistance.
                      </Popover>
                    }
                  >
                    <Link to="#">{formatStatus(row.status)}</Link>
                  </OverlayTrigger>
                )}
            </div>
          </TableCell>

          {/* Wet Lab validation status for the assay */}
          <TableCell padding="none">
            <div>
              {row &&
                row.status &&
                row.status.toLowerCase() === "done" &&
                (row.validationType === "WetLab" ? "Yes" : "No")}
            </div>
          </TableCell>

          {/* Chemistry for the assay */}
          <TableCell padding="none">
            <div>{row.chemistry}</div>
          </TableCell>

          {/* Dyes involved to design assay*/}
          <TableCell padding="none">
            <div className="text-truncate " style={{ maxWidth: 70 }}>
              {row &&
                row.status &&
                row.status.toLowerCase() === "done" &&
                row.dyes &&
                formatFluorophores(row.dyes.join("/").toUpperCase())}
            </div>
          </TableCell>

          {/* Assay ID for the given assay */}
          <TableCell padding="none">
            <div>
              {row &&
                row.status &&
                row.status.toLowerCase() === "done" &&
                row.assayId}
            </div>
          </TableCell>

          {/* Buttons cell for the assay row */}
          <TableCell padding="none">
            <div className="float-right">
              {<DeleteFromMyAssayButton actions={actions} uniqueRowId={id} />}
            </div>
            {row.status && row.status.toLowerCase() === "done" && (
              <div className="buttons float-left d-flex align-items-center flex-column">
                <AddToCartAndQuoteButton
                  row={row}
                  actions={actions}
                  uniqueRowId={id}
                />
              </div>
            )}
          </TableCell>
        </TableRow>

        {hasSupplement && (
          <TableRow
            className={classNames(
              classes.row,
              viewedClass,
              evenOddClass,
              "supplemental-row"
            )}
          >
            <TableCell
              className={classNames(
                row && row.assayFormat && row.assayFormat.toLowerCase()
              )}
              padding="none"
              scope="row"
            >
              &nbsp;
              {/*<div style={{ maxWidth: 1 }}>&nbsp;</div>*/}
            </TableCell>

            <TableCell colSpan="3">
              Vector Backbone Assay selected:{" "}
              {formatUniversalAssayName(row.uniAssayId) &&
                formatUniversalAssayName(row.uniAssayId) !== "None" && (
                  <Link to={`/digital-assays/assay-detail/${row.uniAssayId}`}>
                    {formatUniversalAssayName(row.uniAssayId)}
                  </Link>
                )}
              {formatUniversalAssayName(row.uniAssayId) &&
                formatUniversalAssayName(row.uniAssayId) === "None" && (
                  <span>None</span>
                )}
            </TableCell>

            <TableCell padding="none" colSpan="7">
              In-silico Multiplex Compatible:{" "}
              <Link
                to={`/digital-assays/assay-detail/${row.assayId}#vector-backbone-assays`}
                onClick={(e) => {
                  e.stopPropagation();
                  row.viewed === 0 && actions.assayViewed(row.id);
                }}
                className="nowrap"
              >
                {_.isUndefined(row.uniAssayCompatibility) ||
                  (row.uniAssayCompatibility !== true && <span>No</span>)}
                {row.uniAssayCompatibility &&
                  row.uniAssayCompatibility === true && <span>Yes</span>}
                {row.uniAssayCompatibilityReason && (
                  <span> ({row.uniAssayCompatibilityReason})</span>
                )}
              </Link>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }
}

export const MyAssayRow = (props) => <AssayRow {...props} />;

/*
  PropTypes for the Components AssayRow, DeleteFromMyAssayButton, AddToCartAndQuoteButton Respectively
*/
AssayRow.propTypes = {
  row: PropTypes.object,
  classes: PropTypes.object,
  change: PropTypes.func,
  actions: PropTypes.object,
  assayDetail: PropTypes.object,
  index: PropTypes.number,
};
